// @flow

// App-side limit for stopping a load. Engine retry settings are not changed
export const CANCEL_TIMEOUT = 5000

export const LOAD_BLOCKED = 'load_blocked'
export const CANCEL_TIMED_OUT = 'cancel_timeout'
export const CANCEL_FAILED = 'cancel_failed'

// Error with a code the player error handling can read
export const createPlaybackError = (code: string, cause?: mixed): Object => {
  const error: Object = new Error(code)

  error.code = code
  error.cause = cause

  return error
}

export type LoadGate = {
  isBlocked: () => boolean,
  isActual: (id: ?number) => boolean,
  begin: () => ?number,
  invalidate: () => void,
  cancel: (stop: () => mixed) => Promise<void>,
}

// One gate per engine instance. Every load (first, reload, channel switch) goes through it.
// A cancel that is still running or has failed blocks new loads on this instance,
// so a new load never starts on top of an engine that may still be loading
export const createLoadGate = (): LoadGate => {
  let generation = 0
  let stopping = null
  let cancelResult = null
  let hasFailedCancel = false

  const isBlocked = () => stopping !== null || hasFailedCancel

  const handleStopped = (isFailed: boolean) => {
    stopping = null
    cancelResult = null
    hasFailedCancel = hasFailedCancel || isFailed
  }

  return {
    isBlocked,

    isActual: (id) => id === generation,

    // Load id, or null when the load must not start
    begin: () => {
      if (isBlocked()) return null

      generation += 1

      return generation
    },

    // Makes the current load stale without stopping the engine (unmount)
    invalidate: () => {
      generation += 1
    },

    // Resolves when the engine has stopped. Rejects on a failed stop
    // or when it takes longer than CANCEL_TIMEOUT; the gate stays blocked until the stop ends
    cancel: (stop) => {
      generation += 1

      if (hasFailedCancel) return Promise.reject(createPlaybackError(CANCEL_FAILED))
      if (cancelResult) return cancelResult

      let timer

      const current = new Promise((resolve) => {
        resolve(stop())
      })
      const stopped = current.then(
        () => {
          clearTimeout(timer)

          return handleStopped(false)
        },
        (error) => {
          clearTimeout(timer)
          handleStopped(true)

          throw createPlaybackError(CANCEL_FAILED, error)
        },
      )
      const timeout = new Promise((resolve, reject) => {
        timer = setTimeout(() => reject(createPlaybackError(CANCEL_TIMED_OUT)), CANCEL_TIMEOUT)
      })

      stopping = current
      cancelResult = Promise.race([stopped, timeout])

      return cancelResult
    },
  }
}
